import { ArrowRight, Loader2, ClipboardPaste, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

const FADE_IN_UP = { opacity: 0, y: 20 };
const FADE_IN_SMALL = { opacity: 0, y: 12 };
const FADE_IN_LARGE = { opacity: 0, y: 24 };
const FADE_IN_ONLY = { opacity: 0 };
const ANIMATE_VISIBLE = { opacity: 1, y: 0 };
const ANIMATE_OPACITY_ONLY = { opacity: 1 };
const BADGE_TRANSITION = { duration: 0.6 };
const H1_TRANSITION = { duration: 0.7, delay: 0.05 };
const P_TRANSITION = { duration: 0.6, delay: 0.15 };
const INPUT_TRANSITION = { duration: 0.6, delay: 0.25 };
const BADGES_TRANSITION = { duration: 0.6, delay: 0.4 };

const MARQUEE_ITEMS = [
  "COMPETITOR RESEARCH",
  "CREATIVE INSPIRATION",
  "AD SWIPE FILE",
  "MEDIA BUYING",
  "TREND SPOTTING",
  "CREATOR TOOLS",
];

export default function Hero({ url, setUrl, onSubmit, loading }) {
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) setUrl(text.trim());
    } catch {
      // Clipboard permission denied or unavailable — silent fallback is expected
    }
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-20 pb-24 md:pt-28 md:pb-32 overflow-hidden"
    >
      {/* Background accent shapes */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-24 w-[500px] h-[500px] bg-yellow-500/10 blur-3xl rounded-full" />
        <div className="absolute top-40 right-0 w-[400px] h-[400px] bg-yellow-500/5 blur-3xl rounded-full" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6">
        <motion.div
          initial={FADE_IN_UP}
          animate={ANIMATE_VISIBLE}
          transition={BADGE_TRANSITION}
          className="inline-flex items-center gap-2 border border-zinc-200 dark:border-zinc-800 bg-white/70 dark:bg-zinc-950/60 px-3 py-1.5"
        >
          <div className="w-1.5 h-1.5 bg-yellow-500 animate-glow-pulse" />
          <span className="text-xs font-mono text-zinc-400 dark:text-zinc-600 dark:text-zinc-400 uppercase tracking-widest">
            Live · Trusted by 50,000+ marketers
          </span>
        </motion.div>

        <div className="mt-8 grid grid-cols-12 gap-8 items-end">
          <motion.h1
            initial={FADE_IN_LARGE}
            animate={ANIMATE_VISIBLE}
            transition={H1_TRANSITION}
            className="col-span-12 lg:col-span-9 font-display font-extrabold text-zinc-900 dark:text-white tracking-tight text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl xl:text-[88px] leading-[0.95]"
          >
            Steal winning{" "}
            <span className="bg-yellow-500 text-black px-3">Meta ads</span>{" "}
            before your competitors do.
          </motion.h1>
          <motion.p
            initial={FADE_IN_SMALL}
            animate={ANIMATE_VISIBLE}
            transition={P_TRANSITION}
            className="col-span-12 lg:col-span-3 text-zinc-400 dark:text-zinc-600 dark:text-zinc-400 text-base leading-relaxed lg:pb-4"
          >
            Paste any Facebook Ad Library URL and download the raw HD video +
            thumbnail. No sign-up, no watermark, no waiting.
          </motion.p>
        </div>

        {/* URL input */}
        <motion.div
          initial={FADE_IN_LARGE}
          animate={ANIMATE_VISIBLE}
          transition={INPUT_TRANSITION}
          className="mt-12 border border-zinc-200 dark:border-zinc-800 bg-white/80 dark:bg-zinc-950/80 p-2 flex flex-col md:flex-row gap-2 md:items-center"
        >
          <div className="flex-1 relative flex items-center">
            <div className="hidden md:flex px-4 border-r border-zinc-200 dark:border-zinc-800 h-14 items-center text-xs font-mono text-zinc-500 dark:text-zinc-500 uppercase tracking-widest">
              URL
            </div>
            <input
              data-testid="url-input"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onSubmit()}
              placeholder="https://www.facebook.com/ads/library/?id=..."
              className="flex-1 bg-transparent px-4 h-14 md:h-16 text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-600 focus:outline-none text-base md:text-lg font-mono"
            />
            <button
              type="button"
              onClick={handlePaste}
              data-testid="paste-btn"
              className="hidden md:inline-flex items-center gap-2 h-10 px-3 mr-2 text-xs text-zinc-400 dark:text-zinc-600 dark:text-zinc-400 hover:text-yellow-500 border border-zinc-200 dark:border-zinc-800 hover:border-yellow-500 transition-colors"
            >
              <ClipboardPaste className="w-3.5 h-3.5" />
              Paste
            </button>
          </div>
          <button
            data-testid="get-video-btn"
            onClick={onSubmit}
            disabled={loading}
            className="group inline-flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-400 disabled:opacity-70 disabled:cursor-not-allowed text-black font-bold text-base h-14 md:h-16 px-6 md:px-10 transition-all hover:-translate-y-0.5"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Fetching…
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" strokeWidth={2.5} />
                Get video
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>
        </motion.div>

        {/* Below input mini badges */}
        <motion.div
          initial={FADE_IN_ONLY}
          animate={ANIMATE_OPACITY_ONLY}
          transition={BADGES_TRANSITION}
          className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-500"
        >
          <span className="flex items-center gap-2">
            <span className="text-yellow-500">✓</span> Free forever
          </span>
          <span className="flex items-center gap-2">
            <span className="text-yellow-500">✓</span> HD quality
          </span>
          <span className="flex items-center gap-2">
            <span className="text-yellow-500">✓</span> No login required
          </span>
          <span className="flex items-center gap-2">
            <span className="text-yellow-500">✓</span> Video + thumbnail
          </span>
        </motion.div>
      </div>

      {/* Marquee band */}
      <div className="mt-24 border-y border-zinc-200 dark:border-zinc-800 bg-zinc-900 dark:bg-black overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee py-4">
          {Array.from({ length: 2 }).map((_, dup) => (
            <div key={`marquee-copy-${dup}`} className="flex items-center">
              {MARQUEE_ITEMS.flatMap((txt) => [
                { key: `${txt}-t`, text: txt },
                { key: `${txt}-s`, text: "★" },
              ]).map((it) => (
                <span
                  key={`${dup}-${it.key}`}
                  className="mx-8 font-display font-bold text-2xl md:text-3xl text-zinc-300 dark:text-zinc-700"
                >
                  {it.text}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
